import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ensureGoogleMapsApi } from '../googleMapsLoader';
import {
  MATERIAL_SYMBOL_VIEW_BOX,
  getLocationMaterialSymbol,
  getMaterialSymbolPath,
  getTriplegMaterialSymbol,
  type MaterialSymbolName,
} from './materialSymbols';

type RawGnssPoint = {
  id: string;
  measured_at: string;
  latitude: number;
  longitude: number;
  accuracy?: number | null;
  altitude?: number | null;
  speed?: number | null;
  row_number?: number;
};

type DerivedStaypoint = {
  id: string;
  started_at: string;
  ended_at: string;
  centroid_latitude: number;
  centroid_longitude: number;
  radius_m?: number | null;
  is_activity?: boolean | null;
  effective_location_id?: string | null;
};

type DerivedTripleg = {
  id: string;
  trip_id?: string | null;
  started_at: string;
  ended_at: string;
  effective_mode: string;
  inferred_mode?: string;
  start_latitude?: number | null;
  start_longitude?: number | null;
  end_latitude?: number | null;
  end_longitude?: number | null;
  matched_geometry_polyline?: string | null;
  matched_path?: Array<{ latitude: number; longitude: number }>;
};

type DerivedTrip = {
  id: string;
  started_at: string;
  ended_at: string;
  tripleg_count: number;
};

type DerivedLocation = {
  id: string;
  label?: string | null;
  semantic_type?: string | null;
  centroid_latitude?: number | null;
  centroid_longitude?: number | null;
  radius_m?: number | null;
  primary_poi?: {
    provider_place_id?: string | null;
    display_name?: string | null;
    formatted_address?: string | null;
    latitude?: number | null;
    longitude?: number | null;
  } | null;
};

type DerivedFocusTarget = {
  type: 'location' | 'staypoint' | 'tripleg';
  id: string;
};

type LocationAnalysisMapProps = {
  title: string;
  rawPoints?: RawGnssPoint[];
  staypoints?: DerivedStaypoint[];
  triplegs?: DerivedTripleg[];
  trips?: DerivedTrip[];
  locations?: DerivedLocation[];
  locationExtentBufferM?: number;
  selectedRawPointId?: string | null;
  selectedDerivedTarget?: { type: 'staypoint' | 'tripleg'; id: string } | null;
  playbackPosition?: { latitude: number; longitude: number } | null;
  focusedDerivedTargets?: DerivedFocusTarget[];
  dimUnfocusedDerivedTargets?: boolean;
  height?: number | string;
  className?: string;
};

const EMPTY_RAW_POINTS: RawGnssPoint[] = [];
const EMPTY_STAYPOINTS: DerivedStaypoint[] = [];
const EMPTY_TRIPLEGS: DerivedTripleg[] = [];
const EMPTY_TRIPS: DerivedTrip[] = [];
const EMPTY_LOCATIONS: DerivedLocation[] = [];
const JST_TIMEZONE = 'Asia/Tokyo';

const jstDateTimeFormatter = new Intl.DateTimeFormat('ja-JP', {
  timeZone: JST_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

const TRIP_ROUTE_COLOR = '#1677ff';
const LOCATION_ICON_COLOR = '#fa8c16';
const FOCUSED_DERIVED_COLOR = '#1677ff';
const DIMMED_DERIVED_COLOR = '#bfbfbf';
const SVG_MARKER_SIZE = 30;
const svgMarkerCache = new Map<string, string>();
const SEMANTIC_TYPE_LABELS = new Map<string, string>([
  ['unknown', '未分類'],
  ['home', '家'],
  ['my_home', '自宅'],
  ['work', 'オフィス'],
  ['my_office', '自社オフィス'],
  ['station', '駅'],
  ['transit', '乗換'],
  ['cafe', 'カフェ'],
  ['restaurant', '飲食店'],
  ['gym', 'ジム'],
  ['hospital', '病院'],
  ['school', '学校'],
  ['store', '店舗'],
  ['hair_salon', 'ヘアサロン'],
  ['beauty_salon', 'エステサロン'],
  ['other', 'その他'],
]);
const TRIPLEG_MODE_LABELS = new Map<string, string>([
  ['walk', '徒歩'],
  ['bicycle', '自転車'],
  ['motorcycle', 'バイク'],
  ['car', '自動車'],
  ['taxi', 'タクシー'],
  ['bus', 'バス'],
  ['train', '電車'],
  ['shinkansen', '新幹線'],
  ['airplane', '航空機'],
  ['slow_mobility', '低速移動'],
  ['motorized_mobility', '車両移動'],
  ['fast_mobility', '高速移動'],
  ['bike', '自転車'],
  ['air', '航空機'],
  ['unknown', '不明'],
]);

const normalizeMeasuredAt = (value?: string | null) => {
  if (!value) {
    return null;
  }
  return value
    .trim()
    .replace(/([+-]\d{2}:\d{2})Z$/, '$1')
    .replace(' ', 'T');
};

const parseMeasuredAt = (value?: string | null) => {
  const normalized = normalizeMeasuredAt(value);
  if (!normalized) {
    return null;
  }
  const epochMs = Date.parse(normalized);
  if (Number.isNaN(epochMs)) {
    return null;
  }
  return new Date(epochMs);
};

const formatMeasuredAt = (value?: string | null) => {
  const parsed = parseMeasuredAt(value);
  return parsed ? jstDateTimeFormatter.format(parsed) : '-';
};

const formatDuration = (startedAt?: string | null, endedAt?: string | null) => {
  const start = parseMeasuredAt(startedAt);
  const end = parseMeasuredAt(endedAt);
  if (!start || !end) {
    return '-';
  }
  const totalSeconds = Math.max(0, Math.round((end.getTime() - start.getTime()) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}時間${minutes}分${seconds}秒`;
  }
  if (minutes > 0) {
    return `${minutes}分${seconds}秒`;
  }
  return `${seconds}秒`;
};

const getSemanticTypeLabel = (value?: string | null) => SEMANTIC_TYPE_LABELS.get(value || 'unknown') || value || '未分類';
const getTriplegModeLabel = (value?: string | null) => TRIPLEG_MODE_LABELS.get(value || 'unknown') || value || '不明';

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const getRawPointDisplayNumber = (point: RawGnssPoint, index: number) => point.row_number ?? index + 1;

const LocationAnalysisMap: React.FC<LocationAnalysisMapProps> = ({
  title,
  rawPoints = EMPTY_RAW_POINTS,
  staypoints = EMPTY_STAYPOINTS,
  triplegs = EMPTY_TRIPLEGS,
  trips = EMPTY_TRIPS,
  locations = EMPTY_LOCATIONS,
  locationExtentBufferM = 0,
  selectedRawPointId = null,
  selectedDerivedTarget = null,
  playbackPosition = null,
  focusedDerivedTargets = [],
  dimUnfocusedDerivedTargets = false,
  height = 360,
  className,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const overlaysRef = useRef<any[]>([]);
  const infoWindowsRef = useRef<any[]>([]);
  const selectedOverlayRef = useRef<any | null>(null);
  const selectedInfoWindowRef = useRef<any | null>(null);
  const selectedDerivedOverlayRef = useRef<any | null>(null);
  const selectedDerivedInfoWindowRef = useRef<any | null>(null);
  const playbackMarkerRef = useRef<any | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  const hasData = rawPoints.length > 0 || staypoints.length > 0 || triplegs.length > 0 || locations.length > 0;

  const sortedRawPoints = useMemo(
    () =>
      rawPoints
        .slice()
        .sort((a, b) => (parseMeasuredAt(a.measured_at)?.getTime() ?? 0) - (parseMeasuredAt(b.measured_at)?.getTime() ?? 0)),
    [rawPoints]
  );

  const tripById = useMemo(() => new Map(trips.map((trip) => [trip.id, trip])), [trips]);

  const tripNameById = useMemo(() => new Map(trips.map((trip, index) => [trip.id, `Trip ${index + 1}`])), [trips]);
  const locationById = useMemo(() => new Map(locations.map((location) => [location.id, location])), [locations]);
  const focusedDerivedTargetKeys = useMemo(
    () => new Set(focusedDerivedTargets.map((target) => `${target.type}:${target.id}`)),
    [focusedDerivedTargets],
  );
  const focusModeEnabled = dimUnfocusedDerivedTargets && focusedDerivedTargetKeys.size > 0;
  const isDerivedTargetFocused = useCallback(
    (type: DerivedFocusTarget['type'], id?: string | null) =>
      Boolean(id && focusedDerivedTargetKeys.has(`${type}:${id}`)),
    [focusedDerivedTargetKeys],
  );
  const isDerivedTargetDimmed = useCallback(
    (type: DerivedFocusTarget['type'], id?: string | null) =>
      focusModeEnabled && !isDerivedTargetFocused(type, id),
    [focusModeEnabled, isDerivedTargetFocused],
  );

  useEffect(() => {
    let cancelled = false;

    const initialize = () => {
      if (cancelled || !mapRef.current || !(window as any).google?.maps?.MapTypeId) {
        return;
      }

      if (!mapInstanceRef.current) {
        mapInstanceRef.current = new (window as any).google.maps.Map(mapRef.current, {
          zoom: 13,
          center: { lat: 35.681236, lng: 139.767125 },
          mapTypeId: (window as any).google.maps.MapTypeId.ROADMAP,
        });
      }
      setReady(true);
    };

    void ensureGoogleMapsApi()
      .then(() => {
        if (!cancelled) {
          setLoadError(null);
          initialize();
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setLoadError(error instanceof Error ? error.message : 'Google Maps APIの読み込みに失敗しました');
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!ready || !mapInstanceRef.current || !(window as any).google?.maps) {
      return;
    }

    overlaysRef.current.forEach((overlay) => overlay.setMap?.(null));
    overlaysRef.current = [];
    infoWindowsRef.current.forEach((infoWindow) => infoWindow.close?.());
    infoWindowsRef.current = [];

    if (!hasData) {
      return;
    }

    const googleMaps = (window as any).google.maps;
    const bounds = new googleMaps.LatLngBounds();

    if (sortedRawPoints.length > 0) {
      sortedRawPoints.forEach((point, index) => {
        const displayNumber = getRawPointDisplayNumber(point, index);
        const marker = new googleMaps.Circle({
          strokeColor: '#1677ff',
          strokeOpacity: 0.85,
          strokeWeight: 1,
          fillColor: '#1677ff',
          fillOpacity: 0.4,
          center: { lat: point.latitude, lng: point.longitude },
          radius: Math.max(4, Math.min(point.accuracy ?? 8, 20)),
          map: mapInstanceRef.current,
        });
        const infoWindow = new googleMaps.InfoWindow({
          content: `
            <div style="padding:8px;min-width:180px">
              <strong>${escapeHtml(title)}</strong>
              <div style="margin-top:4px">#${displayNumber}</div>
              <div style="margin-top:4px">${formatMeasuredAt(point.measured_at)}</div>
              <div style="margin-top:4px">${point.latitude.toFixed(6)}, ${point.longitude.toFixed(6)}</div>
              <div style="margin-top:4px">accuracy: ${typeof point.accuracy === 'number' ? `${point.accuracy.toFixed(1)}m` : '-'}</div>
            </div>
          `,
        });
        marker.addListener('click', () => {
          infoWindowsRef.current.forEach((row) => row.close?.());
          infoWindow.setPosition({ lat: point.latitude, lng: point.longitude });
          infoWindow.open(mapInstanceRef.current);
        });
        overlaysRef.current.push(marker);
        infoWindowsRef.current.push(infoWindow);
        bounds.extend({ lat: point.latitude, lng: point.longitude });
      });

      if (sortedRawPoints.length >= 2) {
        const path = sortedRawPoints.map((point) => ({
          lat: point.latitude,
          lng: point.longitude,
        }));
        const polyline = new googleMaps.Polyline({
          path,
          geodesic: true,
          strokeColor: '#69b1ff',
          strokeOpacity: 0.7,
          strokeWeight: 2,
          map: mapInstanceRef.current,
        });
        overlaysRef.current.push(polyline);
      }
    }

    staypoints.forEach((staypoint, index) => {
      const isShortStay = staypoint.is_activity === false;
      const location = staypoint.effective_location_id ? locationById.get(staypoint.effective_location_id) : undefined;
      const focused =
        isDerivedTargetFocused('staypoint', staypoint.id) ||
        isDerivedTargetFocused('location', staypoint.effective_location_id);
      const dimmed =
        focusModeEnabled &&
        !focused &&
        isDerivedTargetDimmed('staypoint', staypoint.id) &&
        isDerivedTargetDimmed('location', staypoint.effective_location_id);
      const baseColor = isShortStay ? '#fa8c16' : '#52c41a';
      const staypointColor = dimmed ? DIMMED_DERIVED_COLOR : baseColor;
      const position = {
        lat: staypoint.centroid_latitude,
        lng: staypoint.centroid_longitude,
      };
      const displayNumber = String(index + 1);
      const marker = new googleMaps.Marker({
        position,
        map: mapInstanceRef.current,
        label: {
          text: displayNumber,
          color: '#ffffff',
          fontSize: displayNumber.length >= 2 ? '10px' : '12px',
          fontWeight: '700',
        },
        icon: {
          path: googleMaps.SymbolPath.CIRCLE,
          fillColor: staypointColor,
          fillOpacity: dimmed ? 0.35 : 0.95,
          scale: focused ? 10 : 8,
          strokeColor: '#ffffff',
          strokeWeight: focused ? 3 : 2,
        },
        opacity: dimmed ? 0.55 : 1,
        zIndex: focused ? 30 : dimmed ? 1 : 10,
      });
      const infoWindow = new googleMaps.InfoWindow({
        content: `
          <div style="padding:8px;min-width:200px">
            <strong>Staypoint ${index + 1}</strong>
            <div style="margin-top:4px">type: ${isShortStay ? 'short / transfer' : 'activity'}</div>
            <div style="margin-top:4px">category: ${escapeHtml(location?.semantic_type || 'unknown')}</div>
            <div style="margin-top:4px">${new Date(staypoint.started_at).toLocaleString()} - ${new Date(staypoint.ended_at).toLocaleString()}</div>
            <div style="margin-top:4px">${staypoint.centroid_latitude.toFixed(6)}, ${staypoint.centroid_longitude.toFixed(6)}</div>
          </div>
        `,
      });
      marker.addListener('click', () => {
        infoWindowsRef.current.forEach((row) => row.close?.());
        infoWindow.open(mapInstanceRef.current, marker);
      });
      overlaysRef.current.push(marker);
      infoWindowsRef.current.push(infoWindow);
      bounds.extend(position);

      if (staypoint.radius_m && staypoint.radius_m > 0) {
        const circle = new googleMaps.Circle({
          strokeColor: staypointColor,
          strokeOpacity: dimmed ? 0.22 : focused ? 0.75 : 0.4,
          strokeWeight: focused ? 2 : 1,
          fillColor: staypointColor,
          fillOpacity: dimmed ? 0.03 : focused ? 0.14 : 0.08,
          center: position,
          radius: staypoint.radius_m,
          map: mapInstanceRef.current,
          zIndex: focused ? 20 : dimmed ? 1 : 10,
        });
        overlaysRef.current.push(circle);
      }
    });

    locations.forEach((location, index) => {
      if (typeof location.centroid_latitude !== 'number' || typeof location.centroid_longitude !== 'number') {
        return;
      }
      const position = {
        lat: location.centroid_latitude,
        lng: location.centroid_longitude,
      };
      const focused = isDerivedTargetFocused('location', location.id);
      const dimmed = isDerivedTargetDimmed('location', location.id);
      const locationColor = dimmed ? DIMMED_DERIVED_COLOR : focused ? FOCUSED_DERIVED_COLOR : LOCATION_ICON_COLOR;
      const label = location.label?.trim() || `Location ${location.id.slice(0, 8)}`;
      const marker = new googleMaps.Marker({
        position,
        map: mapInstanceRef.current,
        zIndex: focused ? 40 : dimmed ? 1 : 12,
        opacity: dimmed ? 0.45 : 1,
        icon: buildSvgMarkerIcon(googleMaps, getLocationMaterialSymbol(location.semantic_type), locationColor),
      });
      const infoWindow = new googleMaps.InfoWindow({
        content: `
          <div style="padding:8px;min-width:220px">
            <strong>${escapeHtml(label)}</strong>
            <div style="margin-top:4px">Location ${index + 1}</div>
            <div style="margin-top:4px">${location.centroid_latitude.toFixed(6)}, ${location.centroid_longitude.toFixed(6)}</div>
            <div style="margin-top:4px">観測半径: ${formatLocationObservedRadius(location.radius_m, locationExtentBufferM)}</div>
            <div style="margin-top:4px">extent半径: ${typeof location.radius_m === 'number' ? `${location.radius_m.toFixed(1)}m` : '-'}</div>
          </div>
        `,
      });
      marker.addListener('click', () => {
        infoWindowsRef.current.forEach((row) => row.close?.());
        infoWindow.open(mapInstanceRef.current, marker);
      });
      overlaysRef.current.push(marker);
      infoWindowsRef.current.push(infoWindow);
      bounds.extend(position);

      if (typeof location.radius_m === 'number' && location.radius_m > 0) {
        const observedRadiusM = Math.max(location.radius_m - locationExtentBufferM, 0);
        if (observedRadiusM > 0) {
          const observedCircle = new googleMaps.Circle({
            strokeColor: locationColor,
            strokeOpacity: dimmed ? 0.2 : focused ? 0.75 : 0.5,
            strokeWeight: focused ? 2 : 1,
            fillColor: locationColor,
            fillOpacity: dimmed ? 0.03 : focused ? 0.14 : 0.1,
            center: position,
            radius: observedRadiusM,
            map: mapInstanceRef.current,
            zIndex: focused ? 20 : dimmed ? 1 : 10,
          });
          overlaysRef.current.push(observedCircle);
        }

        const extentCircle = new googleMaps.Polyline({
          path: buildCirclePath(position, location.radius_m),
          geodesic: true,
          strokeColor: '#fa8c16',
          strokeOpacity: 0,
          strokeWeight: 1,
          icons: [
            {
              icon: {
                path: 'M 0,-1 0,1',
                strokeColor: dimmed ? DIMMED_DERIVED_COLOR : focused ? FOCUSED_DERIVED_COLOR : '#ad4e00',
                strokeOpacity: dimmed ? 0.35 : 0.9,
                scale: 2,
              },
              offset: '0',
              repeat: '12px',
            },
          ],
          map: mapInstanceRef.current,
        });
        overlaysRef.current.push(extentCircle);
      }

    });

    triplegs.forEach((tripleg) => {
      const path = buildTriplegPath(tripleg);
      if (path.length < 2) {
        return;
      }
      const focused = isDerivedTargetFocused('tripleg', tripleg.id);
      const dimmed = isDerivedTargetDimmed('tripleg', tripleg.id);
      const triplegColor = dimmed ? DIMMED_DERIVED_COLOR : focused ? FOCUSED_DERIVED_COLOR : TRIP_ROUTE_COLOR;
      path.forEach((point) => bounds.extend(point));
      const polyline = new googleMaps.Polyline({
        path,
        geodesic: true,
        strokeColor: triplegColor,
        strokeOpacity: dimmed ? 0.25 : 0.88,
        strokeWeight: focused ? 7 : 4,
        zIndex: focused ? 30 : dimmed ? 1 : 10,
        map: mapInstanceRef.current,
      });
      const trip = tripleg.trip_id ? tripById.get(tripleg.trip_id) : undefined;
      const tripLabel = tripleg.trip_id ? tripNameById.get(tripleg.trip_id) || `Trip ${tripleg.trip_id.slice(0, 8)}` : 'Trip 未所属';
      const infoWindow = new googleMaps.InfoWindow({
        content: `
          <div style="padding:8px;min-width:240px">
            <strong>${escapeHtml(tripLabel)}</strong>
            <div style="margin-top:4px">開始: ${formatMeasuredAt(trip?.started_at ?? tripleg.started_at)}</div>
            <div style="margin-top:4px">終了: ${formatMeasuredAt(trip?.ended_at ?? tripleg.ended_at)}</div>
            <div style="margin-top:4px">triplegs: ${trip?.tripleg_count ?? 1}</div>
            <div style="margin-top:4px">mode: ${escapeHtml(tripleg.effective_mode)}</div>
          </div>
        `,
      });
      polyline.addListener('click', (event: any) => {
        infoWindowsRef.current.forEach((row) => row.close?.());
        infoWindow.setPosition(event.latLng);
        infoWindow.open(mapInstanceRef.current);
      });
      overlaysRef.current.push(polyline);
      infoWindowsRef.current.push(infoWindow);
      const middlePoint = path[Math.floor(path.length / 2)];
      const modeMarker = new googleMaps.Marker({
        position: middlePoint,
        map: mapInstanceRef.current,
        zIndex: focused ? 40 : dimmed ? 1 : 14,
        opacity: dimmed ? 0.45 : 1,
        icon: buildSvgMarkerIcon(googleMaps, getTriplegMaterialSymbol(tripleg.effective_mode), triplegColor),
      });
      modeMarker.addListener('click', () => {
        infoWindowsRef.current.forEach((row) => row.close?.());
        infoWindow.setPosition(middlePoint);
        infoWindow.open(mapInstanceRef.current);
      });
      overlaysRef.current.push(modeMarker);
    });

    if (!bounds.isEmpty()) {
      mapInstanceRef.current.fitBounds(bounds);
    }
  }, [
    focusModeEnabled,
    hasData,
    isDerivedTargetDimmed,
    isDerivedTargetFocused,
    locationById,
    locationExtentBufferM,
    locations,
    ready,
    sortedRawPoints,
    staypoints,
    title,
    tripById,
    tripNameById,
    triplegs,
  ]);

  useEffect(() => {
    if (!ready || !mapInstanceRef.current || !(window as any).google?.maps) {
      return;
    }

    selectedOverlayRef.current?.setMap?.(null);
    selectedOverlayRef.current = null;
    selectedInfoWindowRef.current?.close?.();
    selectedInfoWindowRef.current = null;

    if (!selectedRawPointId) {
      return;
    }

    const selectedPoint = sortedRawPoints.find((point) => point.id === selectedRawPointId);
    if (!selectedPoint) {
      return;
    }
    const selectedIndex = sortedRawPoints.findIndex((point) => point.id === selectedRawPointId);
    const selectedDisplayNumber = getRawPointDisplayNumber(selectedPoint, selectedIndex);

    const googleMaps = (window as any).google.maps;
    const position = { lat: selectedPoint.latitude, lng: selectedPoint.longitude };
    mapInstanceRef.current.panTo(position);
    if ((mapInstanceRef.current.getZoom?.() ?? 0) < 17) {
      mapInstanceRef.current.setZoom(17);
    }

    selectedOverlayRef.current = new googleMaps.Marker({
      position,
      map: mapInstanceRef.current,
      zIndex: 999,
      icon: {
        path: googleMaps.SymbolPath.CIRCLE,
        fillColor: '#f5222d',
        fillOpacity: 1,
        scale: 9,
        strokeColor: '#ffffff',
        strokeWeight: 3,
      },
    });
    selectedInfoWindowRef.current = new googleMaps.InfoWindow({
      content: `
        <div style="padding:8px;min-width:220px">
          <strong>${escapeHtml(title)}</strong>
          <div style="margin-top:4px">#${selectedIndex >= 0 ? selectedDisplayNumber : '-'}</div>
          <div style="margin-top:4px">選択した測定点</div>
          <div style="margin-top:4px">${formatMeasuredAt(selectedPoint.measured_at)}</div>
          <div style="margin-top:4px">${selectedPoint.latitude.toFixed(6)}, ${selectedPoint.longitude.toFixed(6)}</div>
        </div>
      `,
    });
    selectedInfoWindowRef.current.open(mapInstanceRef.current, selectedOverlayRef.current);

    return () => {
      selectedOverlayRef.current?.setMap?.(null);
      selectedOverlayRef.current = null;
      selectedInfoWindowRef.current?.close?.();
      selectedInfoWindowRef.current = null;
    };
  }, [ready, selectedRawPointId, sortedRawPoints, title]);

  useEffect(() => {
    if (!ready || !mapInstanceRef.current || !(window as any).google?.maps) {
      return;
    }

    selectedDerivedOverlayRef.current?.setMap?.(null);
    selectedDerivedOverlayRef.current = null;
    selectedDerivedInfoWindowRef.current?.close?.();
    selectedDerivedInfoWindowRef.current = null;

    if (!selectedDerivedTarget) {
      return;
    }

    const googleMaps = (window as any).google.maps;

    if (selectedDerivedTarget.type === 'staypoint') {
      const staypointIndex = staypoints.findIndex((staypoint) => staypoint.id === selectedDerivedTarget.id);
      const staypoint = staypointIndex >= 0 ? staypoints[staypointIndex] : undefined;
      if (!staypoint) {
        return;
      }

      const location = staypoint.effective_location_id ? locationById.get(staypoint.effective_location_id) : undefined;
      const hasLocationPosition =
        typeof location?.centroid_latitude === 'number' && typeof location?.centroid_longitude === 'number';
      const position = hasLocationPosition
        ? { lat: location.centroid_latitude as number, lng: location.centroid_longitude as number }
        : { lat: staypoint.centroid_latitude, lng: staypoint.centroid_longitude };
      const locationLabel =
        location?.label?.trim() ||
        (location ? `Location ${location.id.slice(0, 8)}` : `Staypoint ${staypointIndex + 1}`);
      const locationAddress = location?.primary_poi?.formatted_address?.trim() || '-';
      mapInstanceRef.current.panTo(position);
      if ((mapInstanceRef.current.getZoom?.() ?? 0) < 16) {
        mapInstanceRef.current.setZoom(16);
      }

      selectedDerivedOverlayRef.current = new googleMaps.Marker({
        position,
        map: mapInstanceRef.current,
        zIndex: 1000,
        label: hasLocationPosition ? undefined : `${staypointIndex + 1}`,
        icon: hasLocationPosition
          ? buildSvgMarkerIcon(googleMaps, getLocationMaterialSymbol(location?.semantic_type), '#f5222d')
          : {
              path: googleMaps.SymbolPath.CIRCLE,
              fillColor: '#f5222d',
              fillOpacity: 1,
              scale: 11,
              strokeColor: '#ffffff',
              strokeWeight: 3,
            },
      });
      selectedDerivedInfoWindowRef.current = new googleMaps.InfoWindow({
        content: `
          <div style="padding:8px;min-width:260px">
            <strong>${escapeHtml(locationLabel)}（Staypoint ${staypointIndex + 1}）</strong>
            <div style="margin-top:4px">タイプ: ${escapeHtml(getSemanticTypeLabel(location?.semantic_type))}</div>
            <div style="margin-top:4px">所在地: ${escapeHtml(locationAddress)}</div>
            <div style="margin-top:4px">時間: ${formatDuration(staypoint.started_at, staypoint.ended_at)}</div>
            <div style="margin-top:4px">開始: ${formatMeasuredAt(staypoint.started_at)}</div>
            <div style="margin-top:4px">終了: ${formatMeasuredAt(staypoint.ended_at)}</div>
          </div>
        `,
      });
      selectedDerivedInfoWindowRef.current.open(mapInstanceRef.current, selectedDerivedOverlayRef.current);
      return () => {
        selectedDerivedOverlayRef.current?.setMap?.(null);
        selectedDerivedOverlayRef.current = null;
        selectedDerivedInfoWindowRef.current?.close?.();
        selectedDerivedInfoWindowRef.current = null;
      };
    }

    const triplegIndex = triplegs.findIndex((tripleg) => tripleg.id === selectedDerivedTarget.id);
    const tripleg = triplegIndex >= 0 ? triplegs[triplegIndex] : undefined;
    if (!tripleg) {
      return;
    }

    const path = buildTriplegPath(tripleg);
    if (path.length < 2) {
      return;
    }

    const bounds = new googleMaps.LatLngBounds();
    path.forEach((point) => bounds.extend(point));
    mapInstanceRef.current.fitBounds(bounds);
    selectedDerivedOverlayRef.current = new googleMaps.Polyline({
      path,
      geodesic: true,
      strokeColor: '#0050b3',
      strokeOpacity: 1,
      strokeWeight: 7,
      zIndex: 1000,
      map: mapInstanceRef.current,
    });
    selectedDerivedInfoWindowRef.current = new googleMaps.InfoWindow({
      content: `
        <div style="padding:8px;min-width:240px">
          <strong>${escapeHtml(getTriplegModeLabel(tripleg.effective_mode))}（Tripleg ${triplegIndex + 1}）</strong>
          <div style="margin-top:4px">時間: ${formatDuration(tripleg.started_at, tripleg.ended_at)}</div>
          <div style="margin-top:4px">開始: ${formatMeasuredAt(tripleg.started_at)}</div>
          <div style="margin-top:4px">終了: ${formatMeasuredAt(tripleg.ended_at)}</div>
        </div>
      `,
    });
    selectedDerivedInfoWindowRef.current.setPosition(path[Math.floor(path.length / 2)]);
    selectedDerivedInfoWindowRef.current.open(mapInstanceRef.current);

    return () => {
      selectedDerivedOverlayRef.current?.setMap?.(null);
      selectedDerivedOverlayRef.current = null;
      selectedDerivedInfoWindowRef.current?.close?.();
      selectedDerivedInfoWindowRef.current = null;
    };
  }, [locationById, ready, selectedDerivedTarget, staypoints, triplegs]);

  useEffect(() => {
    if (!ready || !mapInstanceRef.current || !(window as any).google?.maps) {
      return;
    }

    const googleMaps = (window as any).google.maps;
    if (
      !playbackPosition ||
      !Number.isFinite(playbackPosition.latitude) ||
      !Number.isFinite(playbackPosition.longitude)
    ) {
      playbackMarkerRef.current?.setMap?.(null);
      playbackMarkerRef.current = null;
      return;
    }

    const position = {
      lat: playbackPosition.latitude,
      lng: playbackPosition.longitude,
    };
    if (!playbackMarkerRef.current) {
      playbackMarkerRef.current = new googleMaps.Marker({
        position,
        map: mapInstanceRef.current,
        zIndex: 1200,
        icon: {
          path: googleMaps.SymbolPath.CIRCLE,
          fillColor: '#f5222d',
          fillOpacity: 1,
          scale: 8,
          strokeColor: '#ffffff',
          strokeWeight: 3,
        },
      });
      return;
    }

    playbackMarkerRef.current.setPosition(position);
  }, [playbackPosition, ready]);

  if (loadError) {
    return <div style={{ color: '#ff4d4f' }}>{loadError}</div>;
  }

  return (
    <div
      ref={mapRef}
      className={className}
      style={{
        width: '100%',
        height,
        borderRadius: 8,
        background: '#f5f5f5',
        overflow: 'hidden',
      }}
    />
  );
};

function buildTriplegPath(tripleg: DerivedTripleg): Array<{ lat: number; lng: number }> {
  const googleMaps = (window as any).google?.maps;
  if (tripleg.matched_geometry_polyline && googleMaps?.geometry?.encoding?.decodePath) {
    const decoded = googleMaps.geometry.encoding.decodePath(tripleg.matched_geometry_polyline);
    const path = decoded.map((point: any) => ({ lat: point.lat(), lng: point.lng() }));
    if (isValidLatLngPath(path)) {
      return path;
    }
    const polyline6Path = decodePolyline(tripleg.matched_geometry_polyline, 6);
    if (isValidLatLngPath(polyline6Path)) {
      return polyline6Path;
    }
  }
  if (tripleg.matched_path && tripleg.matched_path.length >= 2) {
    return tripleg.matched_path.map((point) => ({
      lat: point.latitude,
      lng: point.longitude,
    }));
  }
  if (
    typeof tripleg.start_latitude === 'number' &&
    typeof tripleg.start_longitude === 'number' &&
    typeof tripleg.end_latitude === 'number' &&
    typeof tripleg.end_longitude === 'number'
  ) {
    return [
      { lat: tripleg.start_latitude, lng: tripleg.start_longitude },
      { lat: tripleg.end_latitude, lng: tripleg.end_longitude },
    ];
  }
  return [];
}

function buildSvgMarkerIcon(googleMaps: any, symbol: MaterialSymbolName, color: string) {
  return {
    url: getSvgMarkerDataUrl(symbol, color),
    scaledSize: new googleMaps.Size(SVG_MARKER_SIZE, SVG_MARKER_SIZE),
    anchor: new googleMaps.Point(SVG_MARKER_SIZE / 2, SVG_MARKER_SIZE / 2),
  };
}

function getSvgMarkerDataUrl(symbol: MaterialSymbolName, color: string) {
  const cacheKey = `${symbol}:${color}`;
  const cached = svgMarkerCache.get(cacheKey);
  if (cached) {
    return cached;
  }
  const svg = buildSvgMarker(symbol, color);
  const dataUrl = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  svgMarkerCache.set(cacheKey, dataUrl);
  return dataUrl;
}

function buildSvgMarker(symbol: MaterialSymbolName, color: string) {
  const iconPath = getMaterialSymbolPath(symbol);
  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="${SVG_MARKER_SIZE}" height="${SVG_MARKER_SIZE}" viewBox="0 0 30 30">
      <circle cx="15" cy="15" r="13" fill="white" fill-opacity="0.92" stroke="${color}" stroke-width="2"/>
      <svg x="5" y="5" width="20" height="20" viewBox="${MATERIAL_SYMBOL_VIEW_BOX}">
        <path d="${iconPath}" fill="${color}"/>
      </svg>
    </svg>
  `.trim();
}

function isValidLatLngPath(path: Array<{ lat: number; lng: number }>): boolean {
  return (
    path.length >= 2 &&
    path.every(
      (point) =>
        Number.isFinite(point.lat) &&
        Number.isFinite(point.lng) &&
        point.lat >= -90 &&
        point.lat <= 90 &&
        point.lng >= -180 &&
        point.lng <= 180,
    )
  );
}

function decodePolyline(encoded: string, precision: number): Array<{ lat: number; lng: number }> {
  const coordinates: Array<{ lat: number; lng: number }> = [];
  const factor = 10 ** precision;
  let index = 0;
  let lat = 0;
  let lng = 0;

  while (index < encoded.length) {
    const nextLat = decodePolylineValue(encoded, index);
    index = nextLat.nextIndex;
    lat += nextLat.value;

    const nextLng = decodePolylineValue(encoded, index);
    index = nextLng.nextIndex;
    lng += nextLng.value;

    coordinates.push({ lat: lat / factor, lng: lng / factor });
  }

  return coordinates;
}

function decodePolylineValue(encoded: string, startIndex: number): { value: number; nextIndex: number } {
  let result = 0;
  let shift = 0;
  let index = startIndex;
  let byte = 0;

  do {
    byte = encoded.charCodeAt(index) - 63;
    index += 1;
    result |= (byte & 0x1f) << shift;
    shift += 5;
  } while (byte >= 0x20 && index < encoded.length);

  return {
    value: result & 1 ? ~(result >> 1) : result >> 1,
    nextIndex: index,
  };
}

function buildCirclePath(
  center: { lat: number; lng: number },
  radiusM: number,
  segments = 96
): Array<{ lat: number; lng: number }> {
  const earthRadiusM = 6371000;
  const latRadians = (center.lat * Math.PI) / 180;
  const lngRadians = (center.lng * Math.PI) / 180;
  const angularDistance = radiusM / earthRadiusM;
  const path: Array<{ lat: number; lng: number }> = [];

  for (let i = 0; i <= segments; i += 1) {
    const bearing = (2 * Math.PI * i) / segments;
    const pointLatRadians = Math.asin(
      Math.sin(latRadians) * Math.cos(angularDistance) +
        Math.cos(latRadians) * Math.sin(angularDistance) * Math.cos(bearing)
    );
    const pointLngRadians =
      lngRadians +
      Math.atan2(
        Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(latRadians),
        Math.cos(angularDistance) - Math.sin(latRadians) * Math.sin(pointLatRadians)
      );
    path.push({
      lat: (pointLatRadians * 180) / Math.PI,
      lng: (pointLngRadians * 180) / Math.PI,
    });
  }

  return path;
}

function formatLocationObservedRadius(value?: number | null, bufferM = 0) {
  if (typeof value !== 'number') {
    return '-';
  }
  return `${Math.max(value - bufferM, 0).toFixed(1)}m`;
}

export default LocationAnalysisMap;
